/**
 * Product type matching the Product Search API response
 */
/**
 * Color variant for a product (different color of same product)
 */
export interface ColorVariant {
  sku: string;
  color: string;
  shopify_product_id?: number;
}

/**
 * Shopify variant mapping - maps size/color/fit combinations to Shopify variant IDs
 * Note: Keys are capitalized (Size, Color, Fit) in the API response
 */
export interface ShopifyVariant {
  Size?: string;
  Color?: string;
  Fit?: string;
  variant_id: number;
  variant_sku?: string;
  inventory_quantity?: number;
  available_for_sale?: boolean;
  price?: number;
  sale_price?: number | null;
  stock_status?: string; // "in_stock" | "low_stock" | "out_of_stock"
}

export interface Product {
  sku: string;
  name: string;
  price: number | null;
  color: string | null;
  description: string | null;
  category_path: string | null;
  gender_or_type: string | null;
  image_url: string | null;
  transparent_image_url?: string | null;
  product_images?: string[];  // Array of additional product images
  material_details: string | null;
  style_aesthetics: string[] | null;
  occasions: string[] | null;
  vibe_summary: string | null;
  color_family: string[] | null;
  formality_level: string | null;
  // Shopify integration
  shopify_product_id?: number;
  shopify_variants?: ShopifyVariant[];
  sale_price?: number | null;
  inventory_quantity?: number;
  available_for_sale?: boolean;
  stock_status?: string;
  // Variant fields (from API)
  product_sizes?: string[];
  all_available_colors?: ColorVariant[];
  // Review/rating data (from product API, string format)
  review_rating?: string | null;
  review_count?: string | null;
  // Review intelligence (nested under review_data, present on products with 10+ reviews)
  review_data?: {
    actual_fit?: string | null;
    comfort_rating?: string | null;
    quality_perception?: string | null;
    recommendation_rate?: number | null;
    review_summary?: string | null;
    review_pros?: string[] | null;
    review_cons?: string[] | null;
  } | null;
}

/**
 * Module-level currency config — set once at widget init, used by all formatPrice calls.
 */
let _currencyConfig = { locale: 'en-US', currency: 'USD' };

/**
 * Set the currency config for formatPrice. Call once at widget init.
 */
export function setCurrencyConfig(locale: string, currency: string) {
  _currencyConfig = { locale, currency };
}

/**
 * Formatted price display
 */
export function formatPrice(price: number | null): string {
  if (price === null) return 'Price unavailable';
  return new Intl.NumberFormat(_currencyConfig.locale, {
    style: 'currency',
    currency: _currencyConfig.currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(price);
}

/**
 * Get the primary category from category_path
 * Works with Shopify hierarchical categories (e.g., "Apparel & Accessories > Clothing > Dresses" → "Dresses")
 */
export function getPrimaryCategory(categoryPath: string | null): string {
  if (!categoryPath) return '';
  const parts = categoryPath.split(' > ');
  return parts[parts.length - 1] || '';
}

/**
 * Parse review_rating string to number. Returns null if invalid, missing, or zero.
 */
export function parseRating(rating: string | null | undefined): number | null {
  if (!rating) return null;
  const parsed = parseFloat(String(rating));
  if (isNaN(parsed) || parsed <= 0 || parsed > 5) return null;
  return parsed;
}

/**
 * Parse review_count string to number. Returns null if invalid or missing.
 */
export function parseReviewCount(count: string | null | undefined): number | null {
  if (!count) return null;
  const parsed = parseInt(String(count), 10);
  if (isNaN(parsed) || parsed < 0) return null;
  return parsed;
}

